import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { setAuthToken } from "./api";
import { useSelector, useDispatch } from "react-redux";
import { login, logout } from "./store/authSlice";
import AppRoutes from "./routes/AppRoutes";

const KEYS = {
  token: "token",
  username: "username",
  isAdmin: "is_admin",
  exp: "expires_at",
};

function readAuthFromStorage() {
  const token = localStorage.getItem(KEYS.token);
  const username = localStorage.getItem(KEYS.username);
  const isAdmin = localStorage.getItem(KEYS.isAdmin) === "true";
  const expiresAt = Number(localStorage.getItem(KEYS.exp));
  return { token, username, isAdmin, expiresAt };
}

function clearAuthStorage() {
  localStorage.removeItem(KEYS.token);
  localStorage.removeItem(KEYS.username);
  localStorage.removeItem(KEYS.isAdmin);
  localStorage.removeItem(KEYS.exp);
}

function App() {
  const token = useSelector((s) => s.auth.token);
  const username = useSelector((s) => s.auth.username);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const prevTokenRef = useRef(null);

  // Init: restore or clear
  useEffect(() => {
    const { token, username, isAdmin, expiresAt } = readAuthFromStorage();
    if (token && username && expiresAt && Date.now() < expiresAt) {
      setAuthToken(token);
      dispatch(
        login({ token, username, is_admin: isAdmin, expires_at: expiresAt })
      );
    } else {
      clearAuthStorage();
      setAuthToken(null);
      dispatch(logout());
    }
  }, [dispatch, navigate]);

  // Keep header in sync
  useEffect(() => {
    setAuthToken(token || null);
  }, [token]);

  // Expiry watchdog (sekundvis)
  useEffect(() => {
    if (!token) return;
    const id = setInterval(() => {
      const exp = Number(localStorage.getItem(KEYS.exp));
      if (exp && Date.now() >= exp) {
        clearAuthStorage();
        setAuthToken(null);
        dispatch(logout());
    
      }
    }, 1000);
    return () => clearInterval(id);
  }, [token, dispatch, navigate]);

  // Cross-tab sync
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === KEYS.token && e.newValue === null) {
        setAuthToken(null);
        dispatch(logout());
  
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [dispatch, navigate]);

  // Navigate to home after logout (any reason)
  useEffect(() => {
    if (prevTokenRef.current && !token) {
      setTimeout(() => navigate("/", { replace: true }), 0);
    }
    prevTokenRef.current = token;
  }, [token, navigate]);

  return <AppRoutes token={token} username={username} />;
}

export default App;
